import { useEffect, useState, useCallback, Fragment } from "react";
import client, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  LogIn, LogOut, Coffee, MapPin, Loader2, CheckCircle2, Clock, ChevronDown,
  Check, X, AlertTriangle, Sunrise, Sunset, Trash2, Plus,
} from "lucide-react";import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { usePoll } from "@/hooks/usePoll";
import { confirm } from "@/components/ConfirmDialog";
import { WorkdayCard } from "@/components/WorkdayCard";
import { GpsWaypoint } from "@/components/GpsWaypoint";
import { PeriodBar, rangeFor, ActiveDot } from "@/components/PeriodBar";
import { useActiveWorkers } from "@/hooks/useActiveWorkers";
import { InlineTime } from "@/components/InlineTime";
import { ClockOutNoteIcon } from "@/components/ClockOutNoteIcon";
import { mergeStaff, hasConnect } from "@/lib/mergeStaff";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtT = (ts) => (ts ? new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—");
// Decimal hours -> "Xh Ym" (e.g. 0.19 -> "0h 11m", 2 -> "2h", 0 -> "0m")
const fmtHrs = (h) => {
  if (h == null || isNaN(h)) return "—";
  const mins = Math.round(Number(h) * 60);
  return `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, "0")}`;
};
// How the clock-in / clock-out was made (phone GPS vs portal vs automatic): [testid, text, amber?]
const sourceTags = (d, t) => {
  const by = (n) => n || "admin";
  const out = [];
  if (d.inSource === "portal") out.push(["in-portal", `${t("att.inPortal", "In: started by")} ${by(d.inByName)} (portal) — no GPS`]);
  if (d.inSource === "phone" && d.inEditedByName) out.push(["in-edited", `${t("att.inEdited", "In: time edited by")} ${d.inEditedByName}`]);
  if (d.outSource === "admin") out.push(["out-admin", `${t("att.outAdmin", "Out: clocked out by")} ${by(d.outByName)} — not checked`]);
  if (d.outSource === "auto") out.push(d.autoNeedsFix
    ? ["out-auto", t("att.outAuto", "Out: auto clock-out — needs review"), true]
    : ["out-auto-fixed", `${t("att.outAutoFixed", "Out: auto clock-out — time corrected by")} ${by(d.outByName)}`]);
  if (d.outSource === "manual") out.push(["out-manual", `${t("att.outManual", "Out: added by")} ${by(d.outByName)} — not checked`]);
  if (d.outSource === "phone" && d.outEdited) out.push(["out-edited", `${t("att.outEdited", "Out: time edited by")} ${by(d.outByName)}`]);
  return out;
};
const ClockSourceTags = ({ d, t }) => (
  <div className="text-[11px] text-slate-500 leading-snug">
    {sourceTags(d, t).map(([id, text, amber], i) => (
      <span key={id}>
        {i > 0 && <span className="mx-1.5 text-slate-300">·</span>}
        <span data-testid={`att-${id}-${d.recordId}`} className={amber ? "rounded bg-amber-50 border border-amber-300 px-1 font-semibold text-amber-800" : ""}>{text}</span>
      </span>
    ))}
  </div>
);

const STATUS_BADGE = {
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
};

// Read-only visibility of temporary/helper worker hours for managers. Reuses
// /api/helpers (server-scoped, money-free). Corrections/removal require a reason.
function HelperPanel({ t, start, end }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  const load = useCallback(() => {
    client.get(`/helpers?start=${start}&end=${end}`)
      .then((r) => { setRows(r.data.helpers || []); setTotal(r.data.totalHelperHours || 0); })
      .catch(() => { setRows([]); setTotal(0); });
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  const remove = async (h) => {
    const reason = window.prompt(t("helper.reason", "Reason for change"));
    if (!reason) return;
    try { await client.post(`/helpers/${h.id}/remove`, { reason }); toast.success(t("helper.remove", "Remove")); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const editEnd = async (h) => {
    const val = window.prompt(t("helper.endTime", "End time") + " (ISO)", h.endTime || new Date().toISOString());
    if (!val) return;
    const reason = window.prompt(t("helper.reason", "Reason for change"));
    if (!reason) return;
    try { await client.patch(`/helpers/${h.id}`, { endTime: val, reason }); toast.success("OK"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!rows.length) return null;
  return (
    <Card className="p-4 border-slate-200" data-testid="helper-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-900">{t("helper.title", "Temporary helpers")}</h3>
        <Badge variant="outline" className="bg-slate-50 text-slate-600">{fmtHrs(total)}</Badge>
      </div>
      <p className="text-xs text-slate-500 mb-3">{t("helper.separate", "Helper hours are tracked separately from your own.")}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50">
              <th className="px-3 py-2">{t("helper.name", "Helper name")}</th>
              <th className="px-3 py-2">{t("cal.date", "Date")}</th>
              <th className="px-3 py-2">{t("helper.start", "Start")}</th>
              <th className="px-3 py-2">{t("helper.stop", "Stop")}</th>
              <th className="px-3 py-2">{t("helper.hours", "hours")}</th>
              <th className="px-3 py-2">{t("helper.recordedBy", "Recorded by")}</th>
              <th className="px-3 py-2 text-right">{t("common.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((h) => (
              <tr key={h.id} data-testid={`helper-row-${h.id}`} className="border-t border-slate-50 hover:bg-slate-50/60">
                <td className="px-3 py-2 font-medium">{h.helperName}
                  {h.flaggedLongRunning && <span className="ml-2 inline-flex items-center gap-0.5 text-xs text-red-600"><AlertTriangle className="h-3 w-3" />{t("helper.longRunning", "Long-running")}</span>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{h.date}</td>
                <td className="px-3 py-2 font-mono text-xs">{fmtT(h.startTime)}</td>
                <td className="px-3 py-2 font-mono text-xs">{h.endTime ? fmtT(h.endTime) : "—"}</td>
                <td className="px-3 py-2 font-medium">{h.hours != null ? fmtHrs(h.hours) : t("helper.active", "Running")}</td>
                <td className="px-3 py-2 text-slate-500">{h.recordedByName}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" data-testid={`helper-edit-${h.id}`} className="h-7 text-slate-600" onClick={() => editEnd(h)}>{t("helper.edit", "Edit")}</Button>
                  <Button size="sm" variant="ghost" data-testid={`helper-remove-${h.id}`} className="h-7 text-red-600" onClick={() => remove(h)}>{t("helper.remove", "Remove")}</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---- Workday card is a shared component (components/WorkdayCard.js) ----

export default function Attendance() {
  const { t } = useI18n();
  const { can, user } = useAuth();
  const canClock = can("attendance.manage");
  const activeIds = useActiveWorkers();
  const canView = can("attendance.view");
  const canApprove = can("timesheet.approve");
  const canDelete = user?.role === "owner" || user?.role === "admin";

  const today = new Date();
  const [date, setDate] = useState(iso(today));
  const [mode, setMode] = useState("day");
  const { start, end } = rangeFor(date, mode);
  const [tab, setTab] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [breakOpen, setBreakOpen] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);
  const [siteF, setSiteF] = useState("all");
  const [supF, setSupF] = useState("all");
  const [workerF, setWorkerF] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ workerId: "", siteId: "none", date: iso(new Date()), startTime: "09:00", endTime: "17:00", reason: "", approve: true });
  const [savingAdd, setSavingAdd] = useState(false);

  useEffect(() => {
    if (!canView) return;
    client.get("/sites").then((r) => setSites(r.data)).catch(() => {});
    client.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, [canView]);

  const load = useCallback((silent = false) => {
    if (!canView) return;
    if (!silent) setLoading(true);
    const params = { start, end };
    if (siteF !== "all") params.siteId = siteF;
    if (supF !== "all") params.supervisorId = supF;
    if (workerF !== "all") params.workerId = workerF;
    client.get("/attendance/summary", { params })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [canView, start, end, siteF, supF, workerF]);
  useEffect(() => { load(); }, [load]);
  usePoll(() => load(true), 15000);

  const dayMatches = (d) => {
    if (tab === "pending") return d.status === "pending";
    if (tab === "approved") return d.status === "approved";
    if (tab === "needs_review") return (d.exceptions?.length || 0) > 0 || d.status === "rejected";
    return true;
  };

  const noFilter = siteF === "all" && supF === "all" && workerF === "all";
  const base = noFilter ? mergeStaff(data?.workers, users, "workerId", "workerName", { totalHours: 0 }, hasConnect) : (data?.workers || []);
  const workers = base
    .map((w) => ({ ...w, visibleDays: (w.days || []).filter(dayMatches) }))
    .filter((w) => (tab === "all" ? true : w.visibleDays.length > 0));

  const toggleSel = (rid) => setSelected((prev) => { const n = new Set(prev); n.has(rid) ? n.delete(rid) : n.add(rid); return n; });

  const decide = async (recordIds, decision) => {
    if (!recordIds.length) return;
    try {
      const { data: res } = await client.post("/attendance/approve", { recordIds, decision });
      toast.success(`${decision === "approved" ? t("att.approved", "Approved") : t("att.rejected", "Rejected")} · ${res.updated}`);
      if (res.blocked?.length) toast.warning(t("att.autoBlocked", `${res.blocked.length} shift(s) skipped — correct the automatic clock-out time first`));
      setSelected(new Set()); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const deleteRecord = async (d) => {
    if (!(await confirm({ title: t("att.delTitle", "Delete attendance record"), description: `${t("att.delConfirm", "Delete this shift")} (${d.date}${d.siteName ? ` · ${d.siteName}` : ""})? ${t("common.cannotUndo", "This cannot be undone.")}`, confirmText: t("common.delete", "Delete"), destructive: true }))) return;
    try { await client.delete(`/attendance/record/${d.recordId}`); toast.success(t("att.deleted", "Record deleted")); setSelected(new Set()); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const isoFromLocal = (day, hhmm) => new Date(`${day}T${hhmm}:00`).toISOString();

  const saveRecordTime = (d, field) => async (hhmm) => {
    try {
      await client.patch(`/attendance/record/${d.recordId}`, { [field]: isoFromLocal(d.date, hhmm), reason: t("att.inlineEdit", "Edited in portal") });
      toast.success(t("common.saved", "Saved")); load(true);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const addBreak = async (d) => {
    const base = d.clockIn ? new Date(d.clockIn) : new Date(`${d.date}T12:00:00`);
    try {
      await client.post("/attendance/break", { recordId: d.recordId, start: base.toISOString(), end: new Date(base.getTime() + 30 * 60000).toISOString(), reason: t("att.inlineAdd", "Added in portal") });
      toast.success(t("att.breakAdded", "Break added — tap the times to adjust")); load(true);
    } catch (er) { toast.error(apiErr(er.response?.data?.detail)); }
  };
  const saveBreakTime = (d, b, field) => async (hhmm) => {
    const day = (b.start || b.end || `${d.date}T00:00:00`).slice(0, 10);
    try {
      await client.patch(`/attendance/break/${b.id}`, { [field]: isoFromLocal(day, hhmm), reason: t("att.inlineEdit", "Edited in portal") });
      toast.success(t("common.saved", "Saved")); load(true);
    } catch (er) { toast.error(apiErr(er.response?.data?.detail)); }
  };
  const deleteBreak = async (b) => {
    if (!(await confirm({ title: t("att.delBreak", "Delete break"), description: t("common.cannotUndo", "This cannot be undone."), confirmText: t("common.delete", "Delete"), destructive: true }))) return;
    try { await client.delete(`/attendance/break/${b.id}?reason=corrected`); toast.success(t("att.deleted", "Record deleted")); load(); }
    catch (er) { toast.error(apiErr(er.response?.data?.detail)); }
  };

  const submitAdd = async () => {
    if (!addForm.workerId || !addForm.date || !addForm.startTime || !addForm.endTime) return toast.error(t("att.addMissing", "Pick a worker, date and times"));
    setSavingAdd(true);
    try {
      await client.post("/attendance/manual", { ...addForm, siteId: addForm.siteId === "none" ? null : addForm.siteId });
      toast.success(t("att.shiftAdded", "Shift added")); setAddOpen(false);
      // Jump the view to the (possibly backdated) shift's date so it's visible right away
      if (addForm.date < start || addForm.date > end) { setDate(addForm.date); setMode("day"); }
      else load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSavingAdd(false); }
  };

  const counts = data?.counts || {};
  const TABS = [
    { k: "all", label: t("att.tab.all", "All") },
    { k: "pending", label: `${t("att.tab.pending", "Pending Approval")}${counts.pending ? ` (${counts.pending})` : ""}` },
    { k: "approved", label: `${t("att.tab.approved", "Approved")}${counts.approved ? ` (${counts.approved})` : ""}` },
    { k: "needs_review", label: `${t("att.tab.review", "Needs Review")}${counts.needsReview ? ` (${counts.needsReview})` : ""}` },
  ];

  return (
    <div className="space-y-4" data-testid="attendance-page">
      <div>
        <h2 className="font-display text-lg font-bold text-brand-ink">{t("nav.attendance", "Attendance")}</h2>
        {canView && <p className="text-sm text-slate-500">{t("att.reviewSubtitleSite", "Static-site attendance — clock in/out at a Site. Field jobs are on the Timesheets page.")}</p>}
      </div>

      {canClock && <WorkdayCard canDelete={canDelete} />}

      {canView && (
        <>
          {/* Filters: site · supervisor (team) · worker */}
          <div className="flex flex-wrap items-center gap-2" data-testid="att-filters">
            <Select value={siteF} onValueChange={setSiteF}>
              <SelectTrigger className="w-52 h-9" data-testid="att-filter-site"><SelectValue placeholder={t("att.filterSite", "All sites")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("att.allSites", "All sites")}</SelectItem>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={supF} onValueChange={setSupF}>
              <SelectTrigger className="w-56 h-9" data-testid="att-filter-supervisor"><SelectValue placeholder={t("att.filterSupervisor", "All supervisors")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("att.allSupervisors", "All supervisors (+ teams)")}</SelectItem>
                {users.filter((u) => u.role === "supervisor").map((u) => <SelectItem key={u.id} value={u.id}>{u.displayName} · {t("att.team", "team")}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={workerF} onValueChange={setWorkerF}>
              <SelectTrigger className="w-56 h-9" data-testid="att-filter-worker"><SelectValue placeholder={t("att.filterWorker", "All users")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("att.allUsers", "All users")}</SelectItem>
                {users.filter((u) => ["cleaner", "supervisor", "office"].includes(u.role)).map((u) => <SelectItem key={u.id} value={u.id}>{u.displayName} ({t(`role.${u.role}`, u.role)})</SelectItem>)}
              </SelectContent>
            </Select>
            {(siteF !== "all" || supF !== "all" || workerF !== "all") && (
              <Button size="sm" variant="ghost" data-testid="att-filter-clear" className="text-slate-500" onClick={() => { setSiteF("all"); setSupF("all"); setWorkerF("all"); }}>{t("att.clearFilters", "Clear filters")}</Button>
            )}
          </div>
          {/* Controls */}
          <PeriodBar date={date} mode={mode} setDate={setDate} setMode={setMode} testid="att-period" />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              {canDelete && <Button size="sm" data-testid="att-add-shift" className="bg-brand hover:bg-brand-dark" onClick={() => setAddOpen(true)}><Plus className="h-4 w-4 mr-1.5" />{t("att.addShift", "Add shift")}</Button>}
            </div>
            <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="att-status-tabs">
              {TABS.map((x) => (
                <button key={x.k} data-testid={`att-tab-${x.k}`} onClick={() => setTab(x.k)}
                  className={`px-3 py-1.5 text-xs font-semibold ${tab === x.k ? "bg-brand text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{x.label}</button>
              ))}
            </div>
          </div>

          {/* Bulk bar */}
          {canApprove && selected.size > 0 && (
            <div className="flex items-center justify-between bg-brand/5 border border-brand/20 rounded-lg px-4 py-2" data-testid="att-bulk-bar">
              <span className="text-sm font-medium text-slate-700">{selected.size} {t("att.selected", "selected")}</span>
              <div className="flex gap-2">
                <Button size="sm" data-testid="att-bulk-approve" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decide([...selected], "approved")}><Check className="h-4 w-4 mr-1.5" />{t("att.approve", "Approve")}</Button>
                <Button size="sm" variant="outline" data-testid="att-bulk-reject" className="text-red-600 border-red-200" onClick={() => decide([...selected], "rejected")}><X className="h-4 w-4 mr-1.5" />{t("att.reject", "Reject")}</Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : workers.length === 0 ? (
            <Card className="p-10 text-center text-slate-400 border-slate-200">{t("att.noneRange", "No attendance in this range.")}</Card>
          ) : (
            <div className="space-y-3">
              {workers.map((w) => {
                const open = expanded[w.workerId];
                return (
                  <Card key={w.workerId} className="border-slate-200 overflow-hidden" data-testid={`att-worker-${w.workerId}`}>
                    <button onClick={() => setExpanded((p) => ({ ...p, [w.workerId]: !p[w.workerId] }))}
                      data-testid={`att-worker-toggle-${w.workerId}`}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-brand to-brand-dark text-white flex items-center justify-center text-xs font-bold uppercase">
                          {(w.workerName || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                        </div>
                        <div className="text-left">
                          <p className="font-semibold text-slate-900 flex items-center gap-1.5"><ActiveDot show={activeIds.has(w.workerId)} />{w.workerName}</p>
                          <p className="text-xs text-slate-500">{w.visibleDays.length} {t("att.records", "records")} · {fmtHrs(w.totalHours)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {w.visibleDays.some((d) => d.withinGeofence === false || d.outWithinGeofence === false) && (
                          <span data-testid={`att-worker-outside-${w.workerId}`} title={t("att.hasOutside", "Clocked in or out outside the geofence")}
                            className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 text-red-600 px-2 py-0.5 text-xs font-semibold">
                            <MapPin className="h-3.5 w-3.5" />{w.visibleDays.filter((d) => d.withinGeofence === false || d.outWithinGeofence === false).length}
                          </span>
                        )}
                        {(w.visibleDays.length > 0 || w.totalHours > 0) && (
                          <span data-testid={`att-worker-has-hours-${w.workerId}`} title={t("att.hasHours", "Has time recorded in this period")}
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 px-2 py-0.5 text-xs font-semibold">
                            <Clock className="h-3.5 w-3.5" />{fmtHrs(w.totalHours)}
                          </span>
                        )}
                        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                      </div>
                    </button>
                    {open && (
                      <div className="border-t border-slate-100 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-400 bg-slate-50">
                              {canApprove && <th className="px-3 py-2 w-8"></th>}
                              <th className="px-3 py-2">{t("att.date", "Date")}</th>
                              <th className="px-3 py-2">{t("att.site", "Site")}</th>
                              <th className="px-3 py-2">{t("att.in", "In")}</th>
                              <th className="px-3 py-2">{t("att.out", "Out")}</th>
                              <th className="px-3 py-2">{t("att.hours", "Hours")}</th>
                              <th className="px-3 py-2">{t("att.break", "Break")}</th>
                              <th className="px-3 py-2">{t("att.geofence", "Geofence")}</th>
                              <th className="px-3 py-2">{t("common.status", "Status")}</th>
                              {(canApprove || canDelete) && <th className="px-3 py-2 text-right">{t("att.actions", "Actions")}</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {w.visibleDays.map((d) => (
                              <Fragment key={d.recordId}>
                              <tr data-testid={`att-record-${d.recordId}`} className="border-t border-slate-50 hover:bg-slate-50/60">
                                {canApprove && <td className="px-3 py-2"><Checkbox data-testid={`att-select-${d.recordId}`} checked={selected.has(d.recordId)} onCheckedChange={() => toggleSel(d.recordId)} disabled={d.status !== "pending"} /></td>}
                                <td className="px-3 py-2 font-medium whitespace-nowrap">{d.date}</td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  <span>{d.siteName}</span>
                                  {d.unscheduled && <span data-testid={`att-unscheduled-${d.recordId}`} className="ml-1.5 inline-flex items-center rounded bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5">{t("att.unscheduled", "Unscheduled")}</span>}
                                  {d.unscheduled && d.reason && <div className="text-[11px] text-slate-500 mt-0.5 max-w-[220px] truncate" title={d.reason}>“{d.reason}”</div>}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap"><InlineTime ts={d.clockIn} editable={canDelete} onSave={saveRecordTime(d, "clockIn")} testid={`att-in-${d.recordId}`} /><GpsWaypoint geo={d.geoIn} workerName={w.workerName} ts={d.clockIn} testid={`att-geoin-${d.recordId}`} /></td>
                                <td className="px-3 py-2 whitespace-nowrap"><InlineTime ts={d.clockOut} editable={canDelete} onSave={saveRecordTime(d, "clockOut")} testid={`att-out-${d.recordId}`} placeholder={t("att.addOut", "+ add")} /><ClockOutNoteIcon notes={d.clockOutNote ? { note: d.clockOutNote, at: d.clockOutNoteAt } : null} testid={`att-note-${d.recordId}`} /><GpsWaypoint geo={d.geoOut} workerName={w.workerName} ts={d.clockOut} testid={`att-geoout-${d.recordId}`} /></td>
                                <td className="px-3 py-2 font-medium whitespace-nowrap">{d.hours != null ? fmtHrs(d.hours) : "—"}</td>
                                <td className="px-3 py-2 text-slate-500">
                                  {d.breakMinutes ? `${d.breakMinutes}m` : "—"}
                                  {canDelete && <button data-testid={`att-breaks-toggle-${d.recordId}`} className="ml-1.5 text-brand hover:underline text-[11px]" onClick={() => setBreakOpen((o) => ({ ...o, [d.recordId]: !o[d.recordId] }))}>{t("att.manage", "manage")}</button>}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap">
                                  {d.unscheduled
                                    ? <span className="inline-flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" />{t("att.noSite", "No site")}</span>
                                    : (d.withinGeofence === false
                                      ? <span className="inline-flex items-center gap-1 text-xs text-red-600"><MapPin className="h-3 w-3" />{t("att.outside", "Outside")}{d.distanceMeters ? ` ${d.distanceMeters}m` : ""}</span>
                                      : d.withinGeofence === true
                                        ? <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><MapPin className="h-3 w-3" />{t("att.within", "Inside")}</span>
                                        : <span data-testid={`att-geo-unchecked-${d.recordId}`} className="inline-flex items-center gap-1 text-xs text-slate-400"><MapPin className="h-3 w-3" />{t("att.notChecked", "Not checked")}</span>)}
                                  {d.outWithinGeofence === false && <span data-testid={`att-out-outside-${d.recordId}`} className="ml-1 inline-flex items-center gap-1 text-xs text-red-600"><MapPin className="h-3 w-3" />{t("att.outsideOut", "Out: outside")}{d.outDistanceMeters ? ` ${d.outDistanceMeters}m` : ""}</span>}
                                  {d.exceptions?.includes("missing_clock_out") && <span className="ml-1 inline-flex items-center gap-0.5 text-xs text-amber-600"><AlertTriangle className="h-3 w-3" />{t("att.noOut", "No clock-out")}</span>}
                                </td>
                                <td className="px-3 py-2"><Badge variant="outline" className={STATUS_BADGE[d.status] || ""}>{t(`att.status.${d.status}`, d.status)}</Badge></td>
                                {(canApprove || canDelete) && (
                                  <td className="px-3 py-2 text-right whitespace-nowrap">
                                    <div className="flex gap-1 justify-end items-center">
                                      {canApprove && d.status === "pending" && (
                                        <>
                                          <Button size="icon" variant="ghost" data-testid={`att-approve-${d.recordId}`} className="h-7 w-7 text-emerald-600" onClick={() => decide([d.recordId], "approved")}><Check className="h-4 w-4" /></Button>
                                          <Button size="icon" variant="ghost" data-testid={`att-reject-${d.recordId}`} className="h-7 w-7 text-red-600" onClick={() => decide([d.recordId], "rejected")}><X className="h-4 w-4" /></Button>
                                        </>
                                      )}
                                      {canApprove && d.status !== "pending" && <span className="text-[11px] text-slate-400">{d.approverName || ""}</span>}
                                      {canDelete && <Button size="icon" variant="ghost" data-testid={`att-delete-${d.recordId}`} className="h-7 w-7 text-red-600 hover:bg-red-50" onClick={() => deleteRecord(d)} title={t("att.delTitle", "Delete attendance record")}><Trash2 className="h-4 w-4" /></Button>}
                                    </div>
                                  </td>
                                )}
                              </tr>
                              {sourceTags(d, t).length > 0 && (
                                <tr data-testid={`att-source-row-${d.recordId}`}><td colSpan={12} className="px-3 pb-2 pt-0">
                                  <ClockSourceTags d={d} t={t} />
                                </td></tr>
                              )}
                              {canDelete && breakOpen[d.recordId] && (
                                <tr className="bg-slate-50/70" data-testid={`att-breaks-row-${d.recordId}`}><td colSpan={12} className="px-4 py-3">
                                  <div className="space-y-3">
                                    <div>
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{t("att.breaks", "Breaks")}</span>
                                        <Button size="sm" variant="outline" className="h-7" data-testid={`att-break-add-${d.recordId}`} onClick={() => addBreak(d)}><Plus className="h-3.5 w-3.5 mr-1" />{t("att.addBreak", "Add break")}</Button>
                                      </div>
                                      {(!d.breaks || d.breaks.length === 0) ? (
                                        <p className="text-xs text-slate-400" data-testid={`att-breaks-empty-${d.recordId}`}>{t("att.noBreaks", "No breaks recorded.")}</p>
                                      ) : (
                                        <div className="space-y-1">
                                          {d.breaks.map((b) => (
                                            <div key={b.id} data-testid={`att-break-${b.id}`} className="flex items-center flex-wrap gap-2 text-xs">
                                              <span className="flex items-center gap-1">
                                                <InlineTime ts={b.start} editable onSave={saveBreakTime(d, b, "start")} testid={`att-break-start-${b.id}`} />
                                                <span className="text-slate-400">–</span>
                                                <InlineTime ts={b.end} editable onSave={saveBreakTime(d, b, "end")} testid={`att-break-end-${b.id}`} placeholder={t("att.open", "open")} />
                                              </span>
                                              <Badge variant="outline" className="bg-white text-slate-600">{b.minutes}m</Badge>
                                              <Button size="icon" variant="ghost" className="h-6 w-6 text-red-600" data-testid={`att-break-del-${b.id}`} onClick={() => deleteBreak(b)}><Trash2 className="h-3.5 w-3.5" /></Button>
                                              {(b.history || []).map((h, hi) => (
                                                <span key={hi} className="text-[11px] text-blue-600">· {h.action === "add" ? t("att.added", "added") : t("att.edited", "edited")} {t("att.by", "by")} {h.by}{h.reason ? ` (${h.reason})` : ""}</span>
                                              ))}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    {(d.editHistory && d.editHistory.length > 0) && (
                                      <div>
                                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{t("att.changeHistory", "Change history")}</span>
                                        <div className="space-y-0.5 mt-1">
                                          {d.editHistory.map((h, hi) => (
                                            <p key={hi} className="text-[11px] text-blue-600" data-testid={`att-hist-${d.recordId}-${hi}`}>
                                              {h.after?.clockIn ? `${t("att.in", "In")} ${fmtT(h.before?.clockIn)} → ${fmtT(h.after?.clockIn)}` : ""}
                                              {h.after?.clockOut ? `${h.after?.clockIn ? ", " : ""}${t("att.out", "Out")} ${fmtT(h.before?.clockOut)} → ${fmtT(h.after?.clockOut)}` : ""}
                                              {" · "}{h.by}{h.reason ? ` · ${h.reason}` : ""}
                                            </p>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </td></tr>
                              )}
                              </Fragment>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
          <HelperPanel t={t} start={start} end={end} />
        </>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="att-add-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("att.addShift", "Add shift")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("att.worker", "Worker")}</Label>
              <Select value={addForm.workerId} onValueChange={(v) => setAddForm({ ...addForm, workerId: v })}>
                <SelectTrigger data-testid="att-add-worker" className="mt-1"><SelectValue placeholder={t("att.selectWorker", "Select worker")} /></SelectTrigger>
                <SelectContent>{users.filter((u) => ["cleaner", "supervisor", "office"].includes(u.role)).map((u) => <SelectItem key={u.id} value={u.id}>{u.displayName}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("att.site", "Site")}</Label>
              <Select value={addForm.siteId} onValueChange={(v) => setAddForm({ ...addForm, siteId: v })}>
                <SelectTrigger data-testid="att-add-site" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">{t("att.unscheduledNoSite", "Unscheduled (no site)")}</SelectItem>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>{t("att.date", "Date")}</Label><Input type="date" data-testid="att-add-date" className="mt-1" value={addForm.date} onChange={(e) => setAddForm({ ...addForm, date: e.target.value })} /></div>
              <div><Label>{t("att.in", "In")}</Label><Input type="time" data-testid="att-add-start" className="mt-1" value={addForm.startTime} onChange={(e) => setAddForm({ ...addForm, startTime: e.target.value })} /></div>
              <div><Label>{t("att.out", "Out")}</Label><Input type="time" data-testid="att-add-end" className="mt-1" value={addForm.endTime} onChange={(e) => setAddForm({ ...addForm, endTime: e.target.value })} /></div>
            </div>
            <div><Label>{t("att.reason", "Reason / note")}</Label><Input data-testid="att-add-reason" className="mt-1" value={addForm.reason} onChange={(e) => setAddForm({ ...addForm, reason: e.target.value })} placeholder={t("att.reasonPh", "e.g. forgot to clock in")} /></div>
            <label className="flex items-center gap-2 text-sm text-slate-700"><Checkbox data-testid="att-add-approve" checked={addForm.approve} onCheckedChange={(v) => setAddForm({ ...addForm, approve: !!v })} />{t("att.markApproved", "Mark approved (counts toward pay)")}</label>
          </div>
          <DialogFooter><Button data-testid="att-add-save" onClick={submitAdd} disabled={savingAdd} className="bg-brand hover:bg-brand-dark">{savingAdd ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}{t("common.save", "Save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
